import { useNavigate, useParams } from 'react-router';
import { useEffect, useState } from 'react';
import { useApolloClient, useQuery, skipToken } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { SxProps } from '@mui/material/styles';
import { mdiArrowULeftTop } from '@mdi/js';
import { useTranslation } from 'react-i18next';

import {
    IPageDrawerProps,
    PageDrawer,
} from 'common/components/PageDrawer/PageDrawer';
import {
    IContentTypeValue,
    ICreatePortfolioItemInput,
    CurrentUserDocument,
    PortfolioItemDocument,
    ISkillDetailFragment,
} from 'graphql/types';
import { useHashMatch } from 'route/hooks/useHashMatch';
import { CustomPortfolioItemContent } from 'user/components/PortfolioItemDrawer/CustomPortfolioItemContent';
import { Loader } from 'common/components/Loader';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { useRouteQuery } from 'route/hooks/useRouteQuery';
import { useApolloError } from 'common/hooks/useApolloError';
import { goToPortfolioPage } from 'user/utils/goToRoutes';
import { CertificatePortfolioItemContent } from 'user/components/PortfolioItemDrawer/CertificatePortfolioItemContent';
import { ExternalContentPortfolioItemContent } from 'user/components/PortfolioItemDrawer/ExternalContentPortfolioItemContent';
import { CoursePortfolioItemContent } from 'user/components/PortfolioItemDrawer/CoursePortfolioItemContent';
import { ScormPortfolioItemContent } from 'user/components/PortfolioItemDrawer/ScormPortfolioItemContent';
import { TrainingPortfolioItemActions } from 'user/components/PortfolioItemDrawer/TrainingPortfolioItemActions';
import { TrainingPortfolioItemContent } from 'user/components/PortfolioItemDrawer/TrainingPortfolioItemContent';
import { OfferEventPortfolioItemContent } from 'user/components/PortfolioItemDrawer/OfferEventPortfolioItemContent';
import { SkillPortfolioItemContent } from 'user/components/PortfolioItemDrawer/SkillPortfolioItemContent';
import { UserDrawerHeader } from 'user/components/UserDrawerHeader';
import { Chip, ChipGroup } from 'common/components/Chip';
import { Button } from 'common/components/Button';
import { Icon } from 'common/components/Icon';
import { NotFound } from 'common/components/NotFound';
import { ShowAfterDelay } from 'common/components/ShowAfterDelay';
import { PortfolioItemDetailsView } from 'user/components/PortfolioItemDrawer/PortfolioItemDetailsView';
import { UserActionBar } from 'user/UserActionBar';
import { IOfferEventEmailOptions } from 'user/components/UserEmailDropdownMenu';
import { ResetPortfolioItemProgressAction } from 'user/actions/ResetPortfolioItemProgressAction';
import { IUpdateGroupProps } from 'user/components/GroupUpdateDrawer';
import { IUserProfileProps } from 'user/components/UserProfileDrawer';
import {
    isSkillPortfolioItem,
    isTrainingPortfolioItem,
} from 'user/utils/portfolioItem';
import { BackgroundImg } from 'common/components/Img';
import { useCanManageUser, useFrontendPermissions } from 'user/hooks';

const externalUserBarSx: SxProps = {
    px: { xs: 2, sm: 4 },
    py: 2,
    borderTop: '1px solid',
    borderBottom: '1px solid',
    borderColor: 'divider',
};

interface IProps extends IPageDrawerProps {
    groupProps?: IUpdateGroupProps;
    pageTitle?: string;
    withEditActions?: boolean;
    portfolioItemId?: string;
    showUser?: boolean;
    hideOfferEventListItem?: boolean;
    onCreateItem?(values: ICreatePortfolioItemInput): void;
    onClose?(): void;
    open?: boolean;
    onResetProgress?(): void;
    userProfileProps?: IUserProfileProps;
}

export const PortfolioItemDrawer = ({
    groupProps,
    portfolioItemId,
    pageTitle,
    withEditActions,
    showUser,
    open,
    hideOfferEventListItem,
    userProfileProps,
    onCreateItem,
    onClose,
    onResetProgress,
}: IProps) => {
    const [translate] = useTranslation();
    const { id: userIdParam, itemId: itemIdParam } = useParams();
    const client = useApolloClient();

    const { showApolloError } = useApolloError();
    const routeQuery = useRouteQuery();
    const navigate = useNavigate();

    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const [filesAreUpdating, setFilesAreUpdating] = useState(false);
    const customPortfolioItemCreate = !!useRouteMatch('PORTFOLIO_ITEM_CREATE');
    const customPortfolioItemEdit = !!useRouteMatch('PORTFOLIO_ITEM_EDIT');
    const userPortfolioItemView = !!useRouteMatch('USER_PORTFOLIO_ITEM_VIEW');
    const portfolioItemViewProfile = !!useRouteMatch('PORTFOLIO_ITEM_VIEW');
    const portfolioItemViewDrawer = !!useHashMatch('PORTFOLIO_ITEM_DRAWER');

    // Portfolio pages of a user
    const isPortfolioTrainingsPage = !!useRouteMatch('PORTFOLIO_TRAININGS');
    const isPortfolioOffersPage = !!useRouteMatch('PORTFOLIO_OFFERS');
    const isPortfolioMeetingsPage = !!useRouteMatch('PORTFOLIO_MEETINGS');
    const isPortfolioSkillsPage = !!useRouteMatch('PORTFOLIO_SKILLS');
    const isUserPortfolioTrainingsPage = !!useRouteMatch('USER_TRAININGS');
    const isUserPortfolioOffersPage = !!useRouteMatch('USER_OFFERS');
    const isUserPortfolioMeetingsPage = !!useRouteMatch('USER_MEETINGS');
    const isUserPortfolioSkillsPage = !!useRouteMatch('USER_PORTFOLIO_SKILLS');

    // Get the portfolio item id from the query string on porfolioItemViewDrawer
    const portfolioItemIdParam = portfolioItemViewDrawer
        ? routeQuery.get('id')
        : undefined;

    const userId = userIdParam || currentUserData?.currentUser?.id;
    const itemId = portfolioItemId || portfolioItemIdParam || itemIdParam;

    const drawerOpen =
        open ||
        customPortfolioItemCreate ||
        customPortfolioItemEdit ||
        userPortfolioItemView ||
        portfolioItemViewProfile;

    const {
        data: portfolioItemData,
        loading: portfolioItemLoading,
        refetch: refetchPortfolioItem,
        error: portfolioItemError,
    } = useQuery(
        PortfolioItemDocument,
        drawerOpen && itemId
            ? { variables: { id: itemId }, fetchPolicy: 'network-only' }
            : skipToken
    );

    useEffect(() => {
        if (!portfolioItemError) return;

        // Do not show error when portfolio item is not found,
        // we handle this by showing the NotFound component
        if (CombinedGraphQLErrors.is(portfolioItemError)) {
            if (
                !portfolioItemError.errors.length ||
                portfolioItemError.errors[0].extensions?.code ===
                    'PortfolioItemNotFound'
            ) {
                return;
            }
        }

        showApolloError(portfolioItemError);
    }, [portfolioItemError, showApolloError]);

    const { portfolioItem } = portfolioItemData || {};

    const isCurrentUserItem =
        !!portfolioItem?.user &&
        portfolioItem.user.id === currentUserData?.currentUser?.id;

    const handleClose = () => {
        if (onClose) {
            onClose?.();

            return;
        }

        goToPortfolioPage(navigate, userIdParam);
    };

    const { relatedType, user, userName, relatedObject } = portfolioItem || {};

    const isSkillType = isSkillPortfolioItem(relatedObject, relatedType);
    const isTrainingType = isTrainingPortfolioItem(relatedObject, relatedType);

    const { canManageUser } = useCanManageUser(user?.id);

    // Only allow resetting progress for course and scorm
    const { canResetProgress: canResetProgressPerm } =
        useFrontendPermissions('portfolioItem');
    const canResetProgress =
        canResetProgressPerm &&
        relatedType &&
        [
            IContentTypeValue.Course,
            IContentTypeValue.Scorm,
            IContentTypeValue.Training,
        ].includes(relatedType);

    let content;

    // Only hide header on the portfolio page of the current user
    const showSkillManagerHeader =
        (isPortfolioSkillsPage || isUserPortfolioSkillsPage) &&
        isSkillType &&
        canManageUser &&
        !(isCurrentUserItem && isPortfolioSkillsPage);

    const hideUserHeader =
        !showSkillManagerHeader &&
        (isPortfolioTrainingsPage ||
            isPortfolioOffersPage ||
            isPortfolioMeetingsPage ||
            isUserPortfolioTrainingsPage ||
            isUserPortfolioOffersPage ||
            isUserPortfolioMeetingsPage ||
            isPortfolioSkillsPage ||
            isUserPortfolioSkillsPage);

    const showUserHeader = (user || userName) && showUser && !hideUserHeader;

    if (!portfolioItemLoading) {
        if (!portfolioItem && !customPortfolioItemCreate) {
            // Use ShowAfterDelay to prevent flickering when loading from cache
            content = (
                <ShowAfterDelay show delay={200}>
                    <NotFound />
                </ShowAfterDelay>
            );
        } else if (!relatedType) {
            content = (
                <CustomPortfolioItemContent
                    filesAreUpdating={filesAreUpdating}
                    loading={portfolioItemLoading}
                    pageTitle={pageTitle}
                    portfolioItem={portfolioItem || undefined}
                    setFilesAreUpdating={setFilesAreUpdating}
                    userId={userId}
                    withEditActions={withEditActions}
                    onClose={() => handleClose()}
                    onCreateItem={(values) => onCreateItem?.(values)}
                    onSubmit={() => handleClose()}
                />
            );
        } else if (portfolioItem) {
            const itemContentProps = {
                filesAreUpdating,
                isCurrentUserItem,
                loading: portfolioItemLoading,
                pageTitle,
                portfolioItem,
                userId,
                withEditActions,
                relatedObject,
                setFilesAreUpdating,
                onClose: () => handleClose(),
                onSubmit: () => handleClose(),
            };

            switch (relatedType) {
                case IContentTypeValue.ReceivedCertificate:
                    content = (
                        <CertificatePortfolioItemContent
                            {...itemContentProps}
                        />
                    );

                    break;
                case IContentTypeValue.ExternalContent:
                    content = (
                        <ExternalContentPortfolioItemContent
                            {...itemContentProps}
                        />
                    );

                    break;
                case IContentTypeValue.Course:
                    content = (
                        <CoursePortfolioItemContent {...itemContentProps} />
                    );

                    break;
                case IContentTypeValue.Scorm:
                    content = (
                        <ScormPortfolioItemContent {...itemContentProps} />
                    );

                    break;
                case IContentTypeValue.Training:
                    content = (
                        <TrainingPortfolioItemContent {...itemContentProps} />
                    );

                    break;
                case IContentTypeValue.OfferEvent:
                    content = (
                        <OfferEventPortfolioItemContent
                            {...itemContentProps}
                            hideOfferEventListItem={hideOfferEventListItem}
                        />
                    );

                    break;
                case IContentTypeValue.Skill:
                    content = (
                        <SkillPortfolioItemContent
                            hideUserBar={!showUserHeader}
                            onUpdate={refetchPortfolioItem}
                            {...itemContentProps}
                        />
                    );

                    break;
                default:
                    content = (
                        <PortfolioItemDetailsView
                            portfolioItem={portfolioItem}
                            userId={userId}
                        />
                    );
            }
        }
    }

    const getPortfolioItemActions = () => {
        if (!relatedObject || !portfolioItem || !userId) return null;

        switch (relatedObject.__typename) {
            case 'TrainingListItem':
                return (
                    <TrainingPortfolioItemActions
                        portfolioItem={portfolioItem}
                    />
                );
            default:
                return null;
        }
    };

    // For skill portfolio items the skill is known, so prefill it in the
    // offer-event dialog — its certificate dropdown is then filtered to the
    // certificates of that skill.
    const skillDetail =
        isSkillType && relatedObject
            ? (relatedObject as ISkillDetailFragment)
            : undefined;
    const offerEventOptions: IOfferEventEmailOptions | undefined = skillDetail
        ? {
              skill: {
                  __typename: 'SkillListItem',
                  id: skillDetail.id,
                  name: skillDetail.name,
              },
          }
        : undefined;

    let headerImage;

    if (isSkillType) {
        const skill = relatedObject as ISkillDetailFragment;

        headerImage = skill?.thumbnail?.url;
    }

    const handleResetProgress = () => {
        if (isTrainingType) {
            // Remove trainingPortfolioReports from Apollo cache
            client.cache.evict({
                fieldName: 'trainingPortfolioReports',
                args: {
                    trainingId: relatedObject.id,
                    userIds: user?.id ? [user.id] : [],
                },
            });

            // Evict participants from training with progress
            client.cache.evict({
                id: `Training:${relatedObject.id}`,
                fieldName: 'participants',
            });

            // Evict the portfolioReport query to ensure fresh data after progress reset
            client.cache.evict({
                id: 'ROOT_QUERY',
                fieldName: 'portfolioReport',
            });

            client.cache.gc();

            // Refetch the portfolio item for training
            refetchPortfolioItem();
        }

        onResetProgress?.();
    };

    const resetProgressAction = canResetProgress && portfolioItem && (
        <ResetPortfolioItemProgressAction
            portfolioItemIds={[portfolioItem.id]}
            skipCacheFieldReset={relatedType === IContentTypeValue.Training}
            translationKey={
                relatedType === IContentTypeValue.Training
                    ? 'resetTrainingPortfolioItemProgress'
                    : undefined
            }
            onSuccess={handleResetProgress}
        >
            <Button
                startIcon={<Icon path={mdiArrowULeftTop} size="1.9rem" />}
                sx={{ flex: 1 }}
                to="USER_PORTFOLIO"
                variant="outlined"
            >
                {translate('reset')}
            </Button>
        </ResetPortfolioItemProgressAction>
    );

    return (
        <PageDrawer
            disableClose={filesAreUpdating}
            headerProps={{ floating: !!headerImage }}
            open={drawerOpen}
            onClose={() => {
                if (filesAreUpdating) return;

                handleClose();
            }}
        >
            {portfolioItemLoading ? (
                <Loader />
            ) : (
                <>
                    {!!headerImage && (
                        <BackgroundImg
                            src={headerImage}
                            sx={{ minHeight: '250px' }}
                        />
                    )}

                    {showUserHeader && (
                        <>
                            <UserDrawerHeader user={user} userName={userName} />
                            {user ? (
                                <UserActionBar
                                    hideChat
                                    hidePortfolio
                                    groupProps={groupProps}
                                    offerEventOptions={offerEventOptions}
                                    userId={user.id}
                                    userProfileProps={userProfileProps}
                                >
                                    {resetProgressAction}
                                    {getPortfolioItemActions()}
                                </UserActionBar>
                            ) : (
                                <Box sx={externalUserBarSx}>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        sx={{
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <ChipGroup>
                                            <Chip
                                                label={translate('external')}
                                            />
                                            <Chip label={translate('lti11')} />
                                        </ChipGroup>
                                        <Box>{resetProgressAction}</Box>
                                    </Stack>
                                </Box>
                            )}
                        </>
                    )}

                    {content}
                </>
            )}
        </PageDrawer>
    );
};
